import { Controller } from "@hotwired/stimulus"

// ── Module catalog ──────────────────────────────────────────────────────────
// configType: null = no configurable params
//             'gsc' = symptoms_to_display + questions
//             'sac' = word_count + skip_delayed_recall
//             'reaction' = error_lower + error_upper + trial_count
//             'vpp' = trial selection + run behavior
//             'vox' = VOX task selection
//             'advanced' = editable module key/title/config
const MODULES = {
  gsc:      { title: 'Graded Symptom Checklist',                configType: 'gsc' },
  sac:      { title: 'Standardized Assessment of Concussion',   configType: 'sac' },
  sac_without_dr: { title: 'SAC w/o Delayed Recall',            configType: 'sacWithoutDr' },
  bess:     { title: 'Balance Assessment',                      configType: null },
  steadi_balance: { title: 'Fall Risk Balance',                 configType: null },
  gait:     { title: 'Tandem Gait',                             configType: null },
  dr:       { title: 'Delayed Recall',                          configType: null },
  srt:      { title: 'Simple Reaction Time',                    configType: 'reaction' },
  crt:      { title: 'Choice Reaction Time',                    configType: 'reaction' },
  trails_a: { title: 'Trail Making Test A',                     configType: null },
  trails_b: { title: 'Trail Making Test B',                     configType: null },
  maps:     { title: 'Processing Speed (MAPS)',                 configType: null },
  va:       { title: 'Visual Acuity',                           configType: null },
  mick:     { title: 'VPP',                                     configType: 'vpp' },
  vox:      { title: 'VOX',                                     configType: 'vox' },
  field_ir: { title: 'Incident Report',                         configType: null },
  ope:      { title: 'Office Physical Exam',                    configType: null },
  biomarker: { title: 'Biomarker',                              configType: null },
  advanced: { title: 'Advanced',                                configType: 'advanced' },
  apsq:     { title: 'APSQ',                                    configType: null },
  smhat:    { title: 'SMHAT',                                   configType: null },
  acft:     { title: 'ACFT',                                    configType: null },
}

const GSC_SYMPTOMS = [
  { key: "headache", label: "Headache" },
  { key: "head_pressure", label: "Pressure in head" },
  { key: "neck_pain", label: "Neck pain" },
  { key: "naus_or_vom", label: "Nausea or vomiting" },
  { key: "dizziness", label: "Dizziness" },
  { key: "blurred_vision", label: "Blurred vision" },
  { key: "balance_problems", label: "Balance problems" },
  { key: "sensitivity_light", label: "Sensitivity to light" },
  { key: "sensitivity_noise", label: "Sensitivity to noise" },
  { key: "slow", label: "Feeling slowed down" },
  { key: "fog", label: 'Feeling like "in a fog"' },
  { key: "not_right", label: '"Don\'t feel right"' },
  { key: "diff_concentrating", label: "Difficulty concentrating" },
  { key: "diff_remembering", label: "Difficulty remembering" },
  { key: "low_energy", label: "Fatigue or low energy" },
  { key: "confusion", label: "Confusion" },
  { key: "drowsiness", label: "Drowsiness" },
  { key: "trouble_falling_asleep", label: "Trouble falling asleep" },
  { key: "more_emotional", label: "More emotional" },
  { key: "irritability", label: "Irritability" },
  { key: "sadness", label: "Sadness" },
  { key: "nervous_or_anxious", label: "Nervous or anxious" },
  { key: "sleeping_more", label: "Sleeping more than usual" },
  { key: "sleeping_less", label: "Sleeping less than usual" },
  { key: "difficulty_sleeping", label: "Difficulty sleeping soundly" },
  { key: "ears_ringing", label: "Ringing in the ears" },
  { key: "numbness_tingling", label: "Numbness or tingling" },
]

const DEFAULT_GSC_CONFIG = {
  symptoms_to_display: GSC_SYMPTOMS.map(symptom => symptom.key),
  questions: "yes",
}

// Default SAC config (standard test parameters)
const DEFAULT_SAC_CONFIG = {
  digits: [
    ["4-9-3",   "6-2-9",   "5-2-6",   "4-1-5"  ],
    ["3-8-1-4", "3-2-7-9", "1-7-9-5", "4-9-6-8"],
    ["6-2-9-7-1","1-5-2-8-6","3-8-5-2-7","6-1-8-4-3"],
    ["7-1-8-4-6-2","5-3-9-1-4-8","8-3-1-9-6-4","7-2-4-8-5-6"],
  ],
  words: [
    "elbow","apple","carpet","saddle","bubble","candle","paper","sugar",
    "sandwich","wagon","baby","monkey","perfume","sunset","iron","finger",
    "penny","blanket","lemon","insect","jacket","arrow","pepper","cotton",
    "movie","dollar","honey","mirror","anchor",
  ],
  word_count: 10,
  skip_delayed_recall: false,
}

const DEFAULT_VPP_CONFIG = {
}

const DEFAULT_VOX_CONFIG = {
  tasks_to_run: ["BASE", "SP", "HVS", "VVS", "NPC", "HVOR", "VVOR", "VMS"],
}

const DEFAULT_ADVANCED_CONFIG = {
  key: "new_module_key",
  title: "New Module",
  config: {},
}

const VOX_TASKS = [
  { key: "BASE", label: "Base" },
  { key: "SP", label: "Smooth Pursuit" },
  { key: "HVS", label: "Horizontal Saccades" },
  { key: "VVS", label: "Vertical Saccades" },
  { key: "NPC", label: "Near Point of Convergence" },
  { key: "HVOR", label: "Horizontal VOR" },
  { key: "VVOR", label: "Vertical VOR" },
  { key: "VMS", label: "Motion Sensitivity" },
]

// Default reaction-time config (SRT defaults; CRT uses 800 for error_upper)
const DEFAULT_REACTION_CONFIG = (key) => ({
  error_lower: 100,
  error_upper: key === 'crt' ? 800 : 500,
  trial_count: 25,
})

// ── Controller ──────────────────────────────────────────────────────────────

export default class extends Controller {
  static targets  = ["stepsList", "stepsJson", "availableModules"]
  static values   = { initialSteps: Array }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  connect() {
    // Deep-clone so we don't mutate the value object
    this.steps = JSON.parse(JSON.stringify(this.initialStepsValue || []))
    this._render()
  }

  // ── Add a module from the catalog ─────────────────────────────────────────

  addModule(event) {
    const key = event.currentTarget.dataset.moduleKey
    if (this.steps.find(s => s.key === key)) return  // already added
    if ((key === 'sac' || key === 'sac_without_dr') && this.steps.find(s => s.key === 'sac')) return

    const mod  = MODULES[key] || { title: key, configType: null }
    const step = { key, title: mod.title }

    if (mod.configType === 'gsc') {
      step.config = JSON.parse(JSON.stringify(DEFAULT_GSC_CONFIG))
    } else if (mod.configType === 'sac' || mod.configType === 'sacWithoutDr') {
      step.config = JSON.parse(JSON.stringify(DEFAULT_SAC_CONFIG))
      if (mod.configType === 'sacWithoutDr') {
        step.key = 'sac'
        step.title = mod.title
        step.config.skip_delayed_recall = true
      }
    } else if (mod.configType === 'reaction') {
      step.config = DEFAULT_REACTION_CONFIG(key)
    } else if (mod.configType === 'vpp') {
      step.config = JSON.parse(JSON.stringify(DEFAULT_VPP_CONFIG))
    } else if (mod.configType === 'vox') {
      step.config = JSON.parse(JSON.stringify(DEFAULT_VOX_CONFIG))
    } else if (mod.configType === 'advanced') {
      step.key = DEFAULT_ADVANCED_CONFIG.key
      step.title = DEFAULT_ADVANCED_CONFIG.title
      step.config = JSON.parse(JSON.stringify(DEFAULT_ADVANCED_CONFIG.config))
      step._advanced = true
      step._configJson = JSON.stringify(step.config, null, 2)
    }

    this.steps.push(step)
    this._render()
  }

  // ── Remove a step ─────────────────────────────────────────────────────────

  removeStep(event) {
    const key   = event.currentTarget.dataset.key
    this.steps  = this.steps.filter(s => s.key !== key)
    this._render()
  }

  // ── Drag-and-drop reordering ──────────────────────────────────────────────

  dragStart(event) {
    this._draggingKey = event.currentTarget.dataset.key
    event.currentTarget.classList.add('dragging')
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', this._draggingKey)
  }

  dragEnd(event) {
    event.currentTarget.classList.remove('dragging')
    this._draggingKey = null
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    event.currentTarget.classList.add('drag-over')
  }

  dragLeave(event) {
    event.currentTarget.classList.remove('drag-over')
  }

  drop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove('drag-over')

    const fromKey = event.dataTransfer.getData('text/plain') || this._draggingKey
    const toKey   = event.currentTarget.dataset.key
    if (!fromKey || fromKey === toKey) return

    const fromIdx = this.steps.findIndex(s => s.key === fromKey)
    const toIdx   = this.steps.findIndex(s => s.key === toKey)
    if (fromIdx < 0 || toIdx < 0) return

    const [moved] = this.steps.splice(fromIdx, 1)
    this.steps.splice(toIdx, 0, moved)
    this._render()
  }

  // ── Config field changes (live — no re-render needed) ─────────────────────

  updateConfig(event) {
    const { key, field, fieldType } = event.currentTarget.dataset
    const step = this.steps.find(s => s.key === key)
    if (!step || !step.config) return

    if (fieldType === 'array-checkbox') {
      const value = event.currentTarget.dataset.value
      const array = Array.isArray(step.config[field]) ? [...step.config[field]] : []
      const parsedValue = event.currentTarget.dataset.valueType === 'number' ? parseInt(value, 10) : value
      if (event.currentTarget.checked && !array.includes(parsedValue)) {
        array.push(parsedValue)
      } else if (!event.currentTarget.checked) {
        const index = array.indexOf(parsedValue)
        if (index >= 0) array.splice(index, 1)
      }
      step.config[field] = array
    } else if (fieldType === 'checkbox') {
      step.config[field] = event.currentTarget.checked
    } else if (fieldType === 'number') {
      step.config[field] = parseInt(event.currentTarget.value, 10) || 0
    } else {
      step.config[field] = event.currentTarget.value
    }
    this._serializeToHidden()
  }

  updateStepField(event) {
    const { key, field } = event.currentTarget.dataset
    const step = this.steps.find(s => s.key === key)
    if (!step) return

    step[field] = event.currentTarget.value.trim()
    this._serializeToHidden()
    this._render()
  }

  updateAdvancedConfig(event) {
    const { key } = event.currentTarget.dataset
    const step = this.steps.find(s => s.key === key)
    if (!step) return

    step._configJson = event.currentTarget.value
    try {
      const parsed = JSON.parse(event.currentTarget.value || '{}')
      step.config = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
      event.currentTarget.classList.remove('is-invalid')
      this._serializeToHidden()
    } catch {
      event.currentTarget.classList.add('is-invalid')
    }
  }

  updateVppMode(event) {
    const { key } = event.currentTarget.dataset
    const step = this.steps.find(s => s.key === key)
    if (!step) return

    if (event.currentTarget.value === 'images') {
      step.config = { trials: 2 }
    } else if (event.currentTarget.value === 'alphanumeric') {
      step.config = { trials: 3 }
    } else {
      step.config = {}
    }

    this._serializeToHidden()
  }

  // Toggle a step's config panel open/closed
  toggleConfig(event) {
    const key   = event.currentTarget.dataset.key
    const panel = this.element.querySelector(`[data-config-panel="${key}"]`)
    if (panel) panel.classList.toggle('d-none')
  }

  // ── Called by form's submit action to populate the hidden field ───────────

  serializeSteps() {
    this._serializeToHidden()
  }

  // ── Private ───────────────────────────────────────────────────────────────

  _serializeToHidden() {
    this.stepsJsonTarget.value = JSON.stringify(this.steps.map(step => {
      const cleanStep = { key: step.key, title: step.title }
      if (step.config && Object.keys(step.config).length > 0) cleanStep.config = step.config
      return cleanStep
    }))
  }

  _render() {
    this._renderSteps()
    this._updateCatalogButtons()
    this._serializeToHidden()
  }

  // Render the selected steps list
  _renderSteps() {
    if (!this.hasStepsListTarget) return

    if (this.steps.length === 0) {
      this.stepsListTarget.innerHTML = `
        <div class="text-center text-muted py-5">
          <div style="font-size:2em;">📋</div>
          <div class="mt-2">No steps yet — click modules on the left to add them.</div>
        </div>`
      return
    }

    this.stepsListTarget.innerHTML = this.steps.map((step, idx) => {
      const mod       = this._moduleForStep(step)
      const hasConfig = !!step.config
      const configHtml = hasConfig ? this._configHtml(step, mod) : ''

      return `
        <div class="wf-step-item card mb-2"
             data-key="${this._esc(step.key)}"
             draggable="true"
             data-action="
               dragstart->workflow-builder#dragStart
               dragend->workflow-builder#dragEnd
               dragover->workflow-builder#dragOver
               dragleave->workflow-builder#dragLeave
               drop->workflow-builder#drop">

          <div class="card-body py-2 px-3 d-flex align-items-center justify-content-between">

            <div class="d-flex align-items-center gap-2">
              <span class="wf-drag-handle text-muted fs-5" title="Drag to reorder">⠿</span>
              <span class="badge bg-secondary rounded-pill" style="min-width:1.8em;">${idx + 1}</span>
              <span class="fw-semibold">${this._esc(step.title)}</span>
              <code class="text-muted" style="font-size:0.75em;">${this._esc(step.key)}</code>
            </div>

            <div class="d-flex align-items-center gap-1">
              ${hasConfig ? `
                <button type="button"
                        class="btn btn-sm btn-outline-secondary"
                        data-action="click->workflow-builder#toggleConfig"
                        data-key="${this._esc(step.key)}">
                  Config ▾
                </button>` : ''}
              <button type="button"
                      class="btn btn-sm btn-outline-danger"
                      data-action="click->workflow-builder#removeStep"
                      data-key="${this._esc(step.key)}"
                      title="Remove step">
                ✕
              </button>
            </div>

          </div>

          ${hasConfig ? `
            <div class="card-footer py-2 px-3 d-none" data-config-panel="${this._esc(step.key)}">
              ${configHtml}
            </div>` : ''}
        </div>`
    }).join('')
  }

  // Render config fields for a step
  _configHtml(step, mod) {
    if (mod.configType === 'gsc') {
      const symptoms = Array.isArray(step.config.symptoms_to_display)
        ? step.config.symptoms_to_display
        : DEFAULT_GSC_CONFIG.symptoms_to_display
      const questions = step.config.questions ?? "yes"

      return `
        <div>
          <div class="row g-3 align-items-end mb-3">
            <div class="col-auto">
              <label class="form-label small mb-1">Questions</label>
              <select class="form-select form-select-sm"
                      data-action="change->workflow-builder#updateConfig"
                      data-key="${this._esc(step.key)}"
                      data-field="questions">
                <option value="yes" ${questions === 'yes' ? 'selected' : ''}>Yes</option>
                <option value="no" ${questions === 'no' ? 'selected' : ''}>No</option>
              </select>
            </div>
          </div>
          <div class="small fw-semibold mb-2">Symptoms</div>
          <div class="row g-2">
            ${GSC_SYMPTOMS.map(symptom => `
              <div class="col-sm-6 col-lg-4">
                <div class="form-check">
                  <input type="checkbox" class="form-check-input"
                         id="gsc-symptom-${symptom.key}-${this._esc(step.key)}"
                         ${symptoms.includes(symptom.key) ? 'checked' : ''}
                         data-action="change->workflow-builder#updateConfig"
                         data-key="${this._esc(step.key)}"
                         data-field="symptoms_to_display" data-field-type="array-checkbox"
                         data-value="${symptom.key}">
                  <label class="form-check-label small" for="gsc-symptom-${symptom.key}-${this._esc(step.key)}">${this._esc(symptom.label)}</label>
                </div>
              </div>
            `).join('')}
          </div>
        </div>`
    }

    if (mod.configType === 'sac' || mod.configType === 'sacWithoutDr') {
      const wc  = step.config.word_count         ?? 10
      const sdr = step.config.skip_delayed_recall ?? false
      return `
        <div class="row g-3 align-items-center">
          <div class="col-auto">
            <label class="form-label small mb-1">Word Count</label>
            <input type="number" class="form-control form-control-sm" style="width:80px;"
                   value="${wc}" min="1" max="10"
                   data-action="change->workflow-builder#updateConfig"
                   data-key="${this._esc(step.key)}"
                   data-field="word_count" data-field-type="number">
          </div>
          <div class="col-auto">
            <div class="form-check mt-3">
              <input type="checkbox" class="form-check-input"
                     id="sdr-${this._esc(step.key)}"
                     ${sdr ? 'checked' : ''}
                     data-action="change->workflow-builder#updateConfig"
                     data-key="${this._esc(step.key)}"
                     data-field="skip_delayed_recall" data-field-type="checkbox">
              <label class="form-check-label small" for="sdr-${this._esc(step.key)}">
                Skip Delayed Recall
              </label>
            </div>
          </div>
          <div class="col-12">
            <small class="text-muted">
              Standard digit sequences and word lists are included automatically.
            </small>
          </div>
        </div>`
    }

    if (mod.configType === 'reaction') {
      const el = step.config.error_lower ?? 100
      const eu = step.config.error_upper ?? 500
      const tc = step.config.trial_count ?? 25
      return `
        <div class="row g-3 align-items-end">
          <div class="col-auto">
            <label class="form-label small mb-1">Error Lower (ms)</label>
            <input type="number" class="form-control form-control-sm" style="width:100px;"
                   value="${el}"
                   data-action="change->workflow-builder#updateConfig"
                   data-key="${this._esc(step.key)}"
                   data-field="error_lower" data-field-type="number">
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1">Error Upper (ms)</label>
            <input type="number" class="form-control form-control-sm" style="width:100px;"
                   value="${eu}"
                   data-action="change->workflow-builder#updateConfig"
                   data-key="${this._esc(step.key)}"
                   data-field="error_upper" data-field-type="number">
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1">Trial Count</label>
            <input type="number" class="form-control form-control-sm" style="width:90px;"
                   value="${tc}" min="1"
                   data-action="change->workflow-builder#updateConfig"
                   data-key="${this._esc(step.key)}"
                   data-field="trial_count" data-field-type="number">
          </div>
        </div>`
    }

    if (mod.configType === 'vpp') {
      const mode = step.config.trials === 2 ? 'images' : step.config.trials === 3 ? 'alphanumeric' : 'full'

      return `
        <div>
          <div class="small fw-semibold mb-2">VPP Mode</div>
          <div class="d-flex flex-wrap gap-3">
            <div class="form-check">
              <input type="radio" class="form-check-input"
                     id="vpp-full-${this._esc(step.key)}"
                     name="vpp-mode-${this._esc(step.key)}"
                     value="full"
                     ${mode === 'full' ? 'checked' : ''}
                     data-action="change->workflow-builder#updateVppMode"
                     data-key="${this._esc(step.key)}">
              <label class="form-check-label small" for="vpp-full-${this._esc(step.key)}">Full</label>
            </div>
            <div class="form-check">
              <input type="radio" class="form-check-input"
                     id="vpp-images-${this._esc(step.key)}"
                     name="vpp-mode-${this._esc(step.key)}"
                     value="images"
                     ${mode === 'images' ? 'checked' : ''}
                     data-action="change->workflow-builder#updateVppMode"
                     data-key="${this._esc(step.key)}">
              <label class="form-check-label small" for="vpp-images-${this._esc(step.key)}">Images Only</label>
            </div>
            <div class="form-check">
              <input type="radio" class="form-check-input"
                     id="vpp-alphanumeric-${this._esc(step.key)}"
                     name="vpp-mode-${this._esc(step.key)}"
                     value="alphanumeric"
                     ${mode === 'alphanumeric' ? 'checked' : ''}
                     data-action="change->workflow-builder#updateVppMode"
                     data-key="${this._esc(step.key)}">
              <label class="form-check-label small" for="vpp-alphanumeric-${this._esc(step.key)}">Alphanumeric Only</label>
            </div>
          </div>
        </div>`
    }

    if (mod.configType === 'vox') {
      const tasks = Array.isArray(step.config.tasks_to_run) ? step.config.tasks_to_run : DEFAULT_VOX_CONFIG.tasks_to_run
      return `
        <div>
          <div class="small fw-semibold mb-2">VOX Tasks</div>
          <div class="row g-2">
            ${VOX_TASKS.map(task => `
              <div class="col-sm-6 col-lg-4">
                <div class="form-check">
                  <input type="checkbox" class="form-check-input"
                         id="vox-task-${task.key}-${this._esc(step.key)}"
                         ${tasks.includes(task.key) ? 'checked' : ''}
                         data-action="change->workflow-builder#updateConfig"
                         data-key="${this._esc(step.key)}"
                         data-field="tasks_to_run" data-field-type="array-checkbox"
                         data-value="${task.key}">
                  <label class="form-check-label small" for="vox-task-${task.key}-${this._esc(step.key)}">${task.label}</label>
                </div>
              </div>
            `).join('')}
          </div>
        </div>`
    }

    if (mod.configType === 'advanced') {
      const configJson = step._configJson ?? JSON.stringify(step.config || {}, null, 2)
      return `
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label small mb-1">Module Key</label>
            <input type="text" class="form-control form-control-sm"
                   value="${this._esc(step.key)}"
                   data-action="change->workflow-builder#updateStepField"
                   data-key="${this._esc(step.key)}"
                   data-field="key">
          </div>
          <div class="col-md-8">
            <label class="form-label small mb-1">Title</label>
            <input type="text" class="form-control form-control-sm"
                   value="${this._esc(step.title)}"
                   data-action="change->workflow-builder#updateStepField"
                   data-key="${this._esc(step.key)}"
                   data-field="title">
          </div>
          <div class="col-12">
            <label class="form-label small mb-1">Config JSON</label>
            <textarea class="form-control form-control-sm font-monospace"
                      rows="6"
                      spellcheck="false"
                      data-action="change->workflow-builder#updateAdvancedConfig"
                      data-key="${this._esc(step.key)}">${this._esc(configJson)}</textarea>
            <div class="invalid-feedback">Enter a valid JSON object.</div>
          </div>
        </div>`
    }

    return ''
  }

  _checkboxHtml(step, field, label) {
    const checked = step.config[field] ?? false
    const id = `${field}-${this._esc(step.key)}`
    return `
      <div class="col-auto">
        <div class="form-check">
          <input type="checkbox" class="form-check-input"
                 id="${id}"
                 ${checked ? 'checked' : ''}
                 data-action="change->workflow-builder#updateConfig"
                 data-key="${this._esc(step.key)}"
                 data-field="${field}" data-field-type="checkbox">
          <label class="form-check-label small" for="${id}">${label}</label>
        </div>
      </div>`
  }

  // Gray out / re-enable catalog buttons based on current selection
  _updateCatalogButtons() {
    if (!this.hasAvailableModulesTarget) return
    const selected = new Set(this.steps.map(s => s.key))

    this.availableModulesTarget
      .querySelectorAll('[data-module-key]')
      .forEach(btn => {
        const key     = btn.dataset.moduleKey
        const inUse   = selected.has(key) || ((key === 'sac' || key === 'sac_without_dr') && selected.has('sac'))
        btn.disabled  = inUse
        btn.classList.toggle('btn-outline-secondary', inUse)
        btn.classList.toggle('btn-outline-primary',   !inUse)
        btn.style.opacity = inUse ? '0.45' : ''
      })
  }

  _moduleForStep(step) {
    if (step._advanced) return MODULES.advanced

    if (step.key === 'sac' && step.config?.skip_delayed_recall && step.title === MODULES.sac_without_dr.title) {
      return MODULES.sac_without_dr
    }

    return MODULES[step.key] || MODULES.advanced
  }

  // Minimal HTML-escape to prevent XSS from module key/title data
  _esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }
}
